/* Type-a-name charm builder.
 *
 * Replaces the letter-by-letter modal with a name field and a strap rendered inline
 * on the page. Each typed character is mapped to the matching charm variant of the
 * chosen colour, so the customer never sees variant plumbing. Picking "Custom" hands
 * off to the original per-charm modal, which stays untouched.
 *
 * Staging goes through the same window.CIKB API the modal uses, so Buy Buttons, the
 * cart hydrator, and the free-charm swap all behave identically either way.
 */
(function () {
  "use strict";

  const MOUNT = "[data-cikb-nameinline]";
  const STAGE_META = "cikb-name-inline";
  /* ROOT CAUSE of the "it resets / goes stale after a variant switch" family:
   * a variant change makes the theme REPLACE the personalisation block, so the
   * closure holding the typed name and the chosen colour is destroyed with it.
   * The old theme never noticed because its inline UI was a single button with no
   * state -- everything lived in the modal. Moving the builder inline put stateful
   * UI inside a container the theme throws away, so the state has to live out here,
   * where it survives, and be restored onto whatever block exists now. */
  const STORE = new Map();

  const isFreeMode = () => !!document.querySelector('[data-free-charms="true"]');
  const uuid = () => (crypto?.randomUUID?.() || "cni-" + Date.now() + Math.random().toString(16).slice(2));

  function readPayload(bid) {
    const el = document.getElementById("cikb-payload-" + bid);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function init(root) {
    try { init_(root); } catch (e) {
      console.warn("[cni] threw", e);
    }
  }
  function init_(root) {
    if (root.dataset.nameMode !== "1") return;
    const mount = root.querySelector(MOUNT);
    if (!mount) return;
    /* The block tears down and rebuilds its own markup when a colour is picked,
     * which empties this mount. Keying only off the ready flag meant the widget
     * refused to come back and the whole personalisation area went blank. */
    if (root.dataset.nameInlineReady === "1" && mount.querySelector(".cni__chip")) return;
    const bid = root.dataset.bid;
    const data = readPayload(bid);
    if (!data || !Array.isArray(data.charms) || !data.charms.length) return;
    root.dataset.nameInlineReady = "1";
    /* When the set includes charms, the modal's "$3 per charm" maths is wrong and
     * confusing — the customer pays nothing for them. */
    if (isFreeMode()) root.classList.add("cikb-free-charms");

    const maxChars = Number(data.maxChars) || 8;
    const colors = data.charms;
    let activeIdx = 0;
    let bundleId = null;

    /* "Mixed": each charm takes a different colour, alternating so no two neighbours
     * match. The sequence is generated once and extended as the name grows, so typing
     * another letter doesn't reshuffle the charms already on the strap. */
    let mixedMode = false;
    const mixedSeq = [];
    function ensureMixed(n) {
      while (mixedSeq.length < n) {
        let c, guard = 0;
        do { c = Math.floor(Math.random() * colors.length); guard++; }
        while (colors.length > 1 && mixedSeq.length &&
               c === mixedSeq[mixedSeq.length - 1] && guard < 20);
        mixedSeq.push(c);
      }
    }
    const colorIdxAt = (i) => (mixedMode ? (mixedSeq[i] != null ? mixedSeq[i] : 0) : activeIdx);
    const baseMap = () => maps[mixedMode ? 0 : activeIdx] || new Map();

    /* letter -> variant, per colour. Titles are single characters plus Heart/Star/
     * Flower; only the single characters are reachable by typing. */
    /* Heart/Star/Flower have multi-word titles, so they can't be typed. Give each a
     * single stand-in character: it occupies exactly one slot in the name field, keeps
     * the maxlength maths honest, and round-trips through the same string the strap
     * and the reorder logic use. */
    const SYMBOLS = [
      { ch: "\u2605", title: "Star",   label: "Add Star" },
      { ch: "\u2665", title: "Heart",  label: "Add Heart" },
      { ch: "\u273F", title: "Flower", label: "Add Flower" }
    ];
    const symbolByChar = new Map(SYMBOLS.map((s) => [s.ch, s.title]));

    /* Fraction of the 300px artwork each glyph actually occupies horizontally,
     * measured from the PNGs. A single negative margin can't suit both "I" (21%)
     * and "Star" (68%) — tuned for letters, the wide shapes collide. Space each
     * charm by its own width instead. */
    const GLYPH_W = {
      A:.70,B:.42,C:.41,D:.42,E:.43,F:.37,G:.50,H:.75,I:.21,J:.39,K:.51,L:.44,M:.64,
      N:.46,O:.48,P:.36,Q:.49,R:.47,S:.42,T:.51,U:.47,V:.45,W:.64,X:.57,Y:.42,Z:.53,
      "\u2665":.65, "\u2605":.68, "\u273F":.60
    };
    const BEAD_BOX = 92;   // must match .cni__bead width in custom.css
    const BEAD_GAP = 4;    // visual breathing room between glyphs
    function beadMargin(ch) {
      const fill = GLYPH_W[ch] != null ? GLYPH_W[ch] : 0.5;
      const slot = BEAD_BOX * fill + BEAD_GAP;
      return (slot - BEAD_BOX) / 2;   // negative: pulls the dead padding back in
    }

    const indexOf = (c) => {
      const m = new Map();
      (c.variants || []).forEach((v) => {
        const t = String(v.title || "").trim();
        if (t.length === 1) m.set(t.toUpperCase(), v);
        const sym = SYMBOLS.find((s) => s.title.toLowerCase() === t.toLowerCase());
        if (sym) m.set(sym.ch, v);
      });
      return m;
    };
    const maps = colors.map(indexOf);

    /* The markup is rendered server-side now (see product-block-customize-it.liquid).
     * Only build it here if something upstream left the mount empty. */
    if (!mount.querySelector(".cni__input")) {
    mount.innerHTML = `
      <div class="cni">
        <div class="cni__row">
          <label class="cni__label" for="cni-input-${bid}">${root.dataset.nameLabel || "Your dog's name"}</label>
          <div class="cni__field">
            <input class="cni__input" id="cni-input-${bid}" type="text" maxlength="${maxChars}"
                   autocomplete="off" placeholder="${root.dataset.namePlaceholder || "Type a name"}" />
            <span class="cni__count" data-cni-count><span data-cni-used>0</span> / ${maxChars} charms</span>
            <button type="button" class="cni__clear" data-cni-clear>Clear</button>
          </div>
        </div>
        <div class="cni__row">
          <span class="cni__label">${root.dataset.customLabel ? "Charm color" : "Charm color"}</span>
          <div class="cni__colors"></div>
        </div>
        <div class="cni__symbols"></div>
        <div class="cni__strap" data-cni-strap aria-live="polite"></div>
      </div>`;
    }

    const colorsEl = mount.querySelector(".cni__colors");
    const symbolsEl = mount.querySelector(".cni__symbols");
    const input    = mount.querySelector(".cni__input");
    const strapEl  = mount.querySelector("[data-cni-strap]");
    const usedEl   = mount.querySelector("[data-cni-used]");
    const countEl  = mount.querySelector("[data-cni-count]");
    /* Typing simply stops at the limit, with nothing to say why. Mark the counter
     * once the collar is full so the cap is visible at the moment it bites. */
    const showCount = (n) => {
      if (usedEl) usedEl.textContent = String(n);
      if (countEl) countEl.classList.toggle("is-max", n >= maxChars);
    };

    /* The collar's own row reads "Collar color: Lilac". Name the charm colour the
     * same way, so the two palettes are told apart by their label rather than by
     * which one happens to sit higher on the page. */
    const colorLabel = colorsEl.parentElement &&
                       colorsEl.parentElement.querySelector(".cni__label");
    /* The chip titles are set in caps ("BABY PINK"); the collar's row is not, so
     * echo them in the same case rather than shouting one of the two. */
    const pretty = (t) =>
      String(t || "").toLowerCase().replace(/(^|[\s/&-])([a-z])/g, (m, a, b) => a + b.toUpperCase());
    const setColorName = (txt) => {
      if (!colorLabel) return;
      txt = pretty(txt);
      let v = colorLabel.querySelector(".cni__value");
      if (!v) {
        v = document.createElement("span");
        v.className = "cni__value";
        colorLabel.appendChild(v);
      }
      v.textContent = txt ? ": " + txt : "";
    };

    // ---------- colour chips ----------
    /* payload.swatches is the COLLAR palette; the charm colours live in payload.pills
     * as c1..c5, index-aligned with payload.charms. */
    const pills = data.pills || {};
    const hexFor = (_label, i) => (pills["c" + (i + 1)] || {}).bg || null;

    colors.forEach((c, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "cni__chip" + (i === activeIdx ? " is-active" : "");
      b.title = c.title || "";
      b.setAttribute("aria-label", c.title || "");
      const hex = hexFor(c.title, i);
      if (hex) b.style.backgroundColor = hex;
      else {
        const img = (c.variants || [])[0]?.image;
        if (img) { b.style.backgroundImage = `url("${img}")`; b.classList.add("has-img"); }
      }
      b.addEventListener("click", () => {
        dropAdopted();
        mixedMode = false;
        activeIdx = i;
        colorsEl.querySelectorAll(".cni__chip").forEach((x) => x.classList.remove("is-active"));
        b.classList.add("is-active");
        setColorName(c.title);
        render();
      });
      colorsEl.appendChild(b);
    });

    /* Mixed: a four-quadrant chip that scatters the palette across the charms. */
    const mixedBtn = document.createElement("button");
    mixedBtn.type = "button";
    mixedBtn.className = "cni__chip cni__chip--mixed";
    mixedBtn.title = "Mixed";
    mixedBtn.setAttribute("aria-label", "Mixed");
    (function paintMixedChip() {
      const quad = [0, 1, 2, 3].map((i) => hexFor(null, i) || "#ccc");
      mixedBtn.style.background =
        `conic-gradient(${quad[0]} 0 25%, ${quad[1]} 0 50%, ${quad[2]} 0 75%, ${quad[3]} 0)`;
    })();
    mixedBtn.addEventListener("click", () => {
      dropAdopted();
      mixedMode = true;
      mixedSeq.length = 0;           // fresh scatter each time Mixed is chosen
      colorsEl.querySelectorAll(".cni__chip").forEach((x) => x.classList.remove("is-active"));
      mixedBtn.classList.add("is-active");
      setColorName("Mixed");
      render();
    });
    colorsEl.appendChild(mixedBtn);

    /* "Custom" hands off to the original modal so per-charm colour picking keeps
     * working exactly as before. */
    const customBtn = document.createElement("button");
    customBtn.type = "button";
    customBtn.className = "cni__chip cni__chip--custom";
    customBtn.textContent = root.dataset.customLabel || "Custom";
    customBtn.addEventListener("click", () => {
      /* Seed the modal with what's already typed so Custom continues the same name
       * instead of opening empty (which made the CTA complain "Pick at least one
       * charm"). Then open through the block's own API rather than clicking the CTA. */
      const api = (window.__cikb || {})[bid];
      const map = baseMap();
      const chars = currentChars();

      if (api && api.state) {
        /* Seed per-charm colours, not one flat colour — otherwise a Mixed strap
         * arrived in the popup as a single colour. */
        api.state.strap = chars.map((ch, i) => {
          const v = (maps[colorIdxAt(i)] || map).get(ch) || map.get(ch);
          return { variantId: v.id, freeVariantId: v.freeId || null,
                   title: ch, img: v.image || "", oos: v.available === false };
        });
        try { api.renderStrap && api.renderStrap(); } catch (e) {}
        try { api.setCount && api.setCount(); } catch (e) {}
      }
      if (api && api.open) api.open();
      else document.dispatchEvent(new CustomEvent("cikb:open", { detail: { bid } }));

      /* Carry the set's current colour into the modal so its strap preview opens on
       * the colour already chosen rather than resetting to the first swatch. */
      setTimeout(() => {
        rebuildModalSwatches(api);
        syncModalSwatch(api);
        /* Same for the CHARM colour: Custom continues from whatever was set
         * before rather than snapping back to the first pill. Clicking the pill
         * rather than poking state keeps the popup's own bookkeeping honest.
         * Mixed has no single colour, so it is left alone. */
        if (!mixedMode) {
          const pills = document.querySelectorAll(`#cikb-colors-${bid} .cikb-color`);
          if (pills[activeIdx]) pills[activeIdx].click();
        }
      }, 60);
    });
    colorsEl.appendChild(customBtn);

    // ---------- staging ----------
    function clearStage() {
      if (bundleId && window.CIKB?.removeBundle) window.CIKB.removeBundle(bundleId);
      bundleId = null;
    }

    /* A set built in the Custom modal stages itself through the same CIKB API, but the
     * inline strap renders from the name field, so nothing appeared to happen — and
     * the staged-set list that would normally show it is hidden in this mode. Adopt
     * the modal's set: show it on the strap and drop our own so exactly one exists. */
    let adoptedId = null;
    const symbolCharForTitle = (t) => {
      const hit = SYMBOLS.find((x) => x.title.toLowerCase() === String(t || "").toLowerCase());
      return hit ? hit.ch : null;
    };

    function renderAdopted(bundle) {
      const disp = bundle.display || [];
      strapEl.innerHTML = "";
      disp.forEach((d) => {
        const bead = document.createElement(d.img ? "img" : "span");
        bead.className = "cni__bead";
        if (d.img) { bead.src = d.img; bead.alt = d.title || ""; }
        else bead.textContent = d.title || "";
        const key = symbolCharForTitle(d.title) || String(d.title || "").toUpperCase();
        bead.style.margin = "0 " + beadMargin(key).toFixed(1) + "px";
        strapEl.appendChild(bead);
      });
      showCount(disp.length);
      input.value = disp.map((d) => d.title || "").join("");
    }

    let adopting = false;

    document.addEventListener("cikb:staged:change", () => {
      if (adopting) return;                       // removeBundle re-emits; don't recurse
      if (!window.CIKB || !window.CIKB.getStagedBundles) return;

      const mine = bundleId;
      const foreign = window.CIKB.getStagedBundles().filter(
        (b) => b && b.meta && String(b.meta.bid) === String(bid) &&
               b.meta.source !== STAGE_META && b.id !== mine
      );
      if (!foreign.length) return;

      /* Take the NEWEST set. Taking the first match meant the already-adopted set kept
       * winning, so a second "Add to set" from the popup was ignored entirely. */
      const latest = foreign[foreign.length - 1];
      if (latest.id === adoptedId) return;

      adopting = true;
      try {
        /* Drop whatever we adopted before, so re-opening the popup replaces the set
         * rather than stacking a second one. */
        if (adoptedId && window.CIKB.removeBundle) window.CIKB.removeBundle(adoptedId);
        foreign.forEach((b) => {
          if (b.id !== latest.id && window.CIKB.removeBundle) window.CIKB.removeBundle(b.id);
        });
        adoptedId = latest.id;
        clearStage();               // our auto-staged set steps aside
        renderAdopted(latest);
        /* Custom is now the live colour state and its per-charm colours override
         * the single-colour choice, so the chip should read as selected. Picking
         * any other chip clears this and drops the adopted set. */
        colorsEl.querySelectorAll(".cni__chip").forEach((x) => x.classList.remove("is-active"));
        customBtn.classList.add("is-active");
        syncCustomFlag(true);
      } finally {
        adopting = false;
      }
    });

    /* Custom is a THIRD colour mode and nothing in this widget said so. Solid lives in
     * activeIdx, Mixed has its own flag, but Custom lived only in the chip's own
     * is-active class -- which the block throws away every time it is rebuilt. The set
     * itself survives in CIKB, so ask CIKB whether it is still there rather than
     * trusting a class that does not outlive a variant change. */
    function adoptedBundle() {
      if (!adoptedId || !window.CIKB || !window.CIKB.getStagedBundles) return null;
      const all = window.CIKB.getStagedBundles() || [];
      for (let i = 0; i < all.length; i++) {
        if (all[i] && all[i].id === adoptedId) return all[i];
      }
      return null;
    }

    /* Publish the mode on the block so the free-leash offer can see it without
     * reading a CSS class that the rebuild has already cleared. */
    function syncCustomFlag(on) {
      if (on) root.dataset.cniCustom = "1";
      else delete root.dataset.cniCustom;
    }

    function dropAdopted() {
      if (adoptedId && window.CIKB && window.CIKB.removeBundle) {
        window.CIKB.removeBundle(adoptedId);
      }
      adoptedId = null;
      syncCustomFlag(false);
    }

    function currentChars() {
      const map = baseMap();
      return Array.from(String(input.value || ""))
        .map((ch) => (symbolByChar.has(ch) ? ch : ch.toUpperCase()))
        .filter((ch) => map.has(ch))
        .slice(0, maxChars);
    }

    function persist() {
      try {
        /* bundleId and adoptedId matter as much as the visible state: they are how
         * this widget finds the set it already staged. Losing them across a block
         * replacement meant clearStage() had nothing to clear, so the previous set
         * stayed staged and the render kept drawing it -- which is why picking a
         * solid colour still came back Mixed. */
        STORE.set(bid, {
          name: input.value, idx: activeIdx, mixed: mixedSeq.slice(),
          mixedMode: mixedMode, bundleId: bundleId, adoptedId: adoptedId,
        });
      } catch (e) {}
    }

    function render() {
      /* An adopted Custom set OWNS the strap: its colours are per-charm and live in the
       * bundle, not in activeIdx. Redrawing from the colour maps here is what repainted
       * a custom strap in one flat colour the moment anything re-rendered the block --
       * the cart was right and the page was not. */
      const ad = adoptedBundle();
      syncCustomFlag(!!ad);
      if (ad) {
        /* renderAdopted rewrites the name field from the bundle's display titles, which
         * is right when the modal first hands a set over and wrong here: a symbol
         * charm's title is a word, not its character, so a rebuild would paste "Heart"
         * into the field. Whatever is in the field now is already correct. */
        const keep = input.value;
        renderAdopted(ad);
        input.value = keep;
        persist();
        return;
      }
      const map = baseMap();
      const chars = currentChars();
      showCount(chars.length);
      if (mixedMode) ensureMixed(chars.length);

      strapEl.innerHTML = "";
      chars.forEach((ch, i) => {
        const v = (maps[colorIdxAt(i)] || map).get(ch) || map.get(ch);
        const bead = document.createElement(v && v.image ? "img" : "span");
        bead.className = "cni__bead";
        if (v && v.image) { bead.src = v.image; bead.alt = ch; }
        else bead.textContent = ch;
        bead.draggable = true;
        bead.dataset.idx = String(i);
        bead.style.margin = "0 " + beadMargin(ch).toFixed(1) + "px";
        attachDrag(bead);
        strapEl.appendChild(bead);
      });

      stage(chars, map);
      /* AFTER staging, not before. stage() is what assigns bundleId, so persisting
       * at the top of render always saved the PREVIOUS id -- and after a block swap
       * clearStage() then removed a bundle that no longer existed while the real one
       * stayed staged. That is the doubling: two sets, one orphaned. */
      persist();
    }

    /* Drag a charm to a new position on the strap; the name field is rewritten to
     * match, so the field and the render never disagree about the order. */
    let dragFrom = null;

    /* Work on the rendered characters, not the raw field. currentChars() filters and
     * normalises, so bead index N and raw-string index N can drift apart — splicing
     * the raw value then reordered the wrong character. */
    function reorder(from, to) {
      const chars = currentChars();
      if (from < 0 || from >= chars.length) return;
      const [moved] = chars.splice(from, 1);
      const at = Math.max(0, Math.min(to, chars.length));
      chars.splice(at, 0, moved);

      /* In Mixed the colour sequence is positional, so moving a charm without moving
       * its colour recoloured everything. Carry each charm's colour with it. */
      if (mixedMode && mixedSeq.length) {
        ensureMixed(chars.length + 1);
        const [movedColor] = mixedSeq.splice(from, 1);
        mixedSeq.splice(at, 0, movedColor);
      }

      input.value = chars.join("");
      dropAdopted();
      render();
    }

    /* Which slot does this X land in? The charms overlap by design (negative margins
     * tuned per glyph), so "whatever element is under the cursor" is unreliable —
     * a wide neighbour often sits on top of the one you can see. Compare against
     * bead centres instead. */
    /* Boundaries captured when the drag starts. Measuring live meant each preview
     * moved the beads, which moved the boundaries under the cursor, so the target
     * flickered between two slots — that's the "slipping". */
    let dragBounds = null;

    function captureBounds() {
      const beads = Array.from(strapEl.querySelectorAll(".cni__bead"));
      const strapRect = strapEl.getBoundingClientRect();
      dragBounds = {
        left: strapRect.left,
        centers: beads.map((b) => {
          const r = b.getBoundingClientRect();
          return r.left + r.width / 2;
        })
      };
    }

    function slotFromX(x) {
      if (!dragBounds) return 0;
      /* Anywhere left of the first charm means the front of the strap — this used to
       * be hard to hit because the boxes overlap well past the visible glyph. */
      if (x <= dragBounds.left) return 0;
      for (let i = 0; i < dragBounds.centers.length; i++) {
        if (x < dragBounds.centers[i]) return i;
      }
      return dragBounds.centers.length;
    }

    function dropAt(x) {
      if (dragFrom === null) return;
      let to = slotFromX(x);
      if (to > dragFrom) to -= 1;          // account for removing the dragged charm
      const from = dragFrom;
      dragFrom = null;
      if (to !== from) reorder(from, to);
      else render();
    }

    /* Live preview: slide the dragged charm into place as the pointer moves, so the
     * strap always shows what you're about to get. Purely DOM — the name field is
     * only rewritten on drop. */
    function previewAt(x) {
      const dragged = strapEl.querySelector(".cni__bead.is-dragging");
      if (!dragged) return;
      let to = slotFromX(x);
      if (to > dragFrom) to -= 1;
      const others = Array.from(strapEl.querySelectorAll(".cni__bead")).filter((b) => b !== dragged);
      const ref = others[to] || null;
      if (ref) { if (dragged.nextSibling !== ref) strapEl.insertBefore(dragged, ref); }
      else if (strapEl.lastChild !== dragged) strapEl.appendChild(dragged);
    }

    function commitPreview() {
      const dragged = strapEl.querySelector(".cni__bead.is-dragging");
      const from = dragFrom;
      dragFrom = null;
      if (!dragged || from === null) { render(); return; }
      const to = Array.from(strapEl.children).indexOf(dragged);
      dragged.classList.remove("is-dragging");
      if (to < 0 || to === from) { render(); return; }
      reorder(from, to);
    }

    strapEl.addEventListener("dragover", (e) => {
      if (dragFrom === null) return;
      e.preventDefault();
      try { e.dataTransfer.dropEffect = "move"; } catch (err) {}
      previewAt(e.clientX);
    });
    strapEl.addEventListener("drop", (e) => {
      if (dragFrom === null) return;
      e.preventDefault();
      commitPreview();
    });

    function attachDrag(bead) {
      bead.addEventListener("dragstart", (e) => {
        dragFrom = Number(bead.dataset.idx);
        captureBounds();
        bead.classList.add("is-dragging");
        try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(dragFrom)); } catch (err) {}
      });
      bead.addEventListener("dragend", () => {
        /* Fires after drop too; if the drop already committed, dragFrom is null and
         * there's nothing to undo. Otherwise the drag was cancelled — re-render to
         * throw the preview away. */
        const cancelled = dragFrom !== null;
        dragFrom = null;
        strapEl.querySelectorAll(".cni__bead").forEach((b) =>
          b.classList.remove("is-dragging", "is-drop-target"));
        if (cancelled) render();
      });


      /* Touch: HTML5 drag events don't fire on mobile, so fall back to pointer. */
      bead.addEventListener("pointerdown", (e) => {
        if (e.pointerType === "mouse") return;
        dragFrom = Number(bead.dataset.idx);
        captureBounds();
        bead.classList.add("is-dragging");
        const move = (ev) => { ev.preventDefault(); previewAt(ev.clientX); };
        const up = () => {
          document.removeEventListener("pointermove", move);
          document.removeEventListener("pointerup", up);
          commitPreview();
        };
        document.addEventListener("pointermove", move);
        document.addEventListener("pointerup", up);
      });
    }

    function stage(chars, map) {
      if (adoptedId) return;   // the modal's set is the live one
      clearStage();
      if (!chars.length || !window.CIKB?.stageBundle) return;

      const freeMode = isFreeMode();
      bundleId = uuid();
      const bundleTitle = (data.bundleTitlePrefix || "Charms") + " — Bundle";

      const items = chars.map((ch, idx) => {
        let v = (maps[colorIdxAt(idx)] || map).get(ch) || map.get(ch);
        /* Not every charm colour has a $0 mirror (Baby Pink has none at the time of
         * writing, and Mixed puts it in the rotation). Staging the paid variant would
         * put one $3 charm in a set the customer was told is free -- which is exactly
         * what the cart showed. Take the same letter from the first colour that does
         * have a mirror instead. */
        if (freeMode && v && !v.freeId) {
          for (let mi = 0; mi < maps.length; mi++) {
            const alt = maps[mi] && maps[mi].get(ch);
            if (alt && alt.freeId) { v = alt; break; }
          }
        }
        const useFree = freeMode && v.freeId;
        return {
          id: String(useFree ? v.freeId : v.id),
          quantity: 1,
          properties: {
            bundleId,
            bundleParentVariantId: data.parentVariantGid || "",
            bundleTitle,
            _Letter: ch,
            _OrderIndex: String(idx),
            _IsPreorder: v.available === false ? "1" : "0",
            ...(useFree ? { _FreeCharm: "1" } : {})
          }
        };
      });

      window.CIKB.stageBundle({
        id: bundleId,
        items,
        display: chars.map((ch, i) => ({
          title: ch,
          img: (((maps[colorIdxAt(i)] || map).get(ch)) || map.get(ch) || {}).image || "",
          oos: false
        })),
        meta: { bid, count: items.length, bundleTitle, source: STAGE_META }
      });
    }

    function currentSetColorLabel() {
      const active = document.querySelector(
        '.mpb-shared-color .mpb-swatch.is-active, .mpb-opt--color .mpb-swatch.is-active'
      );
      return active ? (active.getAttribute('aria-label') || '').trim() : '';
    }

    /* Resolve the flat strap colour for a swatch: explicit override wins, else the
     * swatch's solid base (never its split artwork). */
    /* Resolve a colour by NAME, reading whichever swatch element is on the page right
     * now. The colour row is torn down and rebuilt on every selection, so anything
     * holding a getComputedStyle reference from earlier is pointing at a detached
     * node and silently returns nothing — which left the modal stuck on its last
     * colour. */
    function sourceSwatchFill(label) {
      const rows = document.querySelectorAll(
        '.mpb-shared-color .mpb-opt-row, .mpb-opt--color .mpb-opt-row'
      );
      const want = String(label || "").trim().toLowerCase();
      for (const row of rows) {
        for (const sw of row.querySelectorAll('.mpb-swatch')) {
          if ((sw.getAttribute('aria-label') || '').trim().toLowerCase() === want) {
            return sw.querySelector('.mpb-swatch__fill') || sw;
          }
        }
      }
      return null;
    }

    /* A printed colourway has a purpose-cut REPEAT tile in Settings > Files, emitted as
     * window.CIKB_STRAP_TILES by product-block-customize-it.liquid. Tiling it is what makes
     * it read as webbing. The reason the strap was flattened to a solid before is that the
     * artwork was painted with background-size:cover, i.e. STRETCHED — which turns a two-tone
     * swatch into a hard half-and-half band. A true repeat unit tiled at `auto 100%` does not
     * distort, so patterned colourways can keep their artwork while splits keep their solid. */
    function tileFor(label) {
      const m = window.CIKB_STRAP_TILES || {};
      return m[String(label || "").trim().toLowerCase()] || "";
    }

    /* The modal strap is a ::before, so it takes no inline style, and its fill cannot travel
     * through the --cikb-preview custom property either: handed a background shorthand the
     * colour arrives every time and the image never does. Write a real rule and win on
     * specificity with a leading html, matching the live theme. The selector has to keep the
     * block-root id AND the class — dropping the class loses to the theme's own rule. */
    function paintModalStrap(modal, hex, tile) {
      if (!modal) return;
      const colour = hex || "#47B276";
      modal.style.setProperty("--cikb-preview", colour);
      const bar = modal.querySelector(".cikb-strap__bar");
      const bid = bar && bar.id ? bar.id.replace(/^cikb-strap-/, "") : "";
      if (!bid) return;
      const sheetId = "cikb-strap-fill-" + bid;
      let st = document.getElementById(sheetId);
      if (!st) { st = document.createElement("style"); st.id = sheetId; document.head.appendChild(st); }
      st.textContent =
        "html #cikb-" + bid + " .cikb-strap__bar::before{"
        + "background-color:" + colour + " !important;"
        + "background-image:" + (tile ? 'url("' + tile + '")' : "none") + " !important;"
        + "background-size:auto 100% !important;"
        + "background-repeat:repeat !important;"
        + "background-position:left center !important;}";
    }

    function strapSolidFor(label) {
      const o = strapOverrides.get(String(label || "").toLowerCase());
      if (o && /^(#|rgb|hsl)/i.test(o)) return o;
      const fill = sourceSwatchFill(label);
      return fill ? (getComputedStyle(fill).backgroundColor || "") : "";
    }

    /* The modal shipped with its own hand-configured swatch list, which didn't match
     * the collar palette — no Black, no Limited Edition patterns, and labels that
     * didn't line up, so pushing a pick back to the collar silently did nothing.
     * Rebuild the row from the real collar swatches so both directions agree. */
    function rebuildModalSwatches(api) {
      const modal = api && api.els && api.els.modal;
      if (!modal) return;
      const row = modal.querySelector(".cikb-preview-swatches");
      if (!row) return;
      /* Take ONE row only. Querying both selectors returned the shared row plus every
       * per-card row (collar and leash), tripling the palette. */
      const sourceRow =
        document.querySelector(".mpb-shared-color .mpb-opt-row") ||
        document.querySelector(".mpb-opt--color .mpb-opt-row");
      const source = sourceRow ? sourceRow.querySelectorAll(".mpb-swatch") : [];
      if (!source.length) return;
      const sig = Array.from(source).map((n) => n.getAttribute('aria-label')).join('|');
      if (row.dataset.mpbSynced === sig) return;
      row.dataset.mpbSynced = sig;

      row.innerHTML = "";
      source.forEach((sw) => {
        const label = (sw.getAttribute("aria-label") || "").trim();
        if (!label) return;
        const fill = sw.querySelector(".mpb-swatch__fill");
        const cs = fill ? getComputedStyle(fill) : null;

        const n = document.createElement("button");
        n.type = "button";
        n.className = "sw";
        n.setAttribute("aria-label", label);
        n.setAttribute("data-label", label);
        n.title = label;
        if (cs) {
          n.style.backgroundColor = cs.backgroundColor;
          n.style.setProperty("--sw", cs.backgroundColor);
          /* Patterns and split colours keep their artwork HERE — it's a round chip,
           * unlike the strap where stretching it looked wrong. */
          if (cs.backgroundImage && cs.backgroundImage !== "none") {
            n.style.backgroundImage = cs.backgroundImage;
            n.style.backgroundSize = "cover";
            n.style.backgroundPosition = "center";
          }
        }
        if (sw.classList.contains("is-oos")) n.classList.add("is-oos");

        n.addEventListener("click", () => {
          row.querySelectorAll(".sw").forEach((x) => x.classList.remove("is-active"));
          n.classList.add("is-active");
          paintModalStrap(modal, strapSolidFor(label), tileFor(label));
          if (window.MPB && typeof window.MPB.setColor === "function") {
            window.MPB.setColor(label);
          }
          setTimeout(paintStrapBackground, 30);
        });
        row.appendChild(n);
      });
    }

    /* Select the modal's preview swatch that matches the set's colour. */
    function syncModalSwatch(api) {
      const label = currentSetColorLabel();
      if (!label || !api || !api.els || !api.els.modal) return;
      const row = api.els.modal.querySelector('.cikb-preview-swatches');
      if (!row) return;
      const nodes = Array.from(row.querySelectorAll('.sw'));
      if (!nodes.length) return;
      let idx = nodes.findIndex((n) =>
        (n.getAttribute('aria-label') || n.getAttribute('data-label') || '')
          .trim().toLowerCase() === label.toLowerCase());
      if (idx < 0) {
        const labels = (data.swatches || []).map((sw) => String(sw.label || '').toLowerCase());
        idx = labels.indexOf(label.toLowerCase());
      }
      /* Repaint first: even a colour with no matching chip must update the strap. */
      paintModalStrap(api.els.modal, strapSolidFor(label), tileFor(label));

      if (idx < 0 || !nodes[idx]) return;
      nodes[idx].classList.add('is-active');
      nodes.forEach((n, i) => { if (i !== idx) n.classList.remove('is-active'); });
    }

    /* The strap IS the collar, so paint it with the colour the set is set to —
     * solid hex, or the swatch artwork for patterned colours. */
    /* "Colour name | #hex" or "Colour name | https://…" lines from the block setting. */
    const strapOverrides = (() => {
      const m = new Map();
      const raw = (document.querySelector('[data-strap-colors]') || {}).dataset?.strapColors || "";
      raw.split(/[\r\n]+/).forEach((line) => {
        const i = line.indexOf("|");
        if (i < 0) return;
        const k = line.slice(0, i).trim().toLowerCase();
        const v = line.slice(i + 1).trim();
        if (k && v) m.set(k, v);
      });
      return m;
    })();

    function paintStrapBackground() {
      const sw = document.querySelector(
        '.mpb-shared-color .mpb-swatch.is-active, .mpb-opt--color .mpb-swatch.is-active'
      );
      if (!sw) return;
      /* strapEl belongs to the block this closure was built against, and a variant
       * change replaces that block -- the delayed listeners below can land after the
       * swap, on a node that is gone. */
      if (!strapEl) return;
      const fill = sw.querySelector('.mpb-swatch__fill') || sw;
      const label = (sw.getAttribute('aria-label') || '').trim().toLowerCase();

      strapEl.style.backgroundSize = "cover";
      strapEl.style.backgroundPosition = "center";

      const override = strapOverrides.get(label);
      if (override && !/^(#|rgb|hsl)/i.test(override)) {
        strapEl.style.backgroundImage = `url("${override}")`;
        strapEl.style.backgroundColor = "";
        return;
      }

      /* A real repeat tile is painted as a tile, not stretched. Everything without one
       * keeps the solid: a two-tone swatch (Sky/Green) is a split image, and stretched
       * across a long strap it reads as a hard half-and-half band. */
      const tile = tileFor(label);
      if (tile) {
        strapEl.style.backgroundImage = 'url("' + tile + '")';
        strapEl.style.backgroundRepeat = "repeat";
        strapEl.style.backgroundSize = "auto 100%";
        strapEl.style.backgroundPosition = "left center";
        strapEl.style.backgroundColor = strapSolidFor(label) || "";
        return;
      }

      const solid = strapSolidFor(label);
      strapEl.style.backgroundColor = solid;
      strapEl.style.backgroundImage = "";
      /* Hand the colour to the cart, which has no way to work out the collar colour
       * from variant ids alone. */
      /* Only when nothing better owns it. This resolves the colour from the modal's
       * own swatch list, which flattens a two-tone colourway to one half; where the
       * 3D block is on the page it knows the real webbing hex and claims the key. */
      try {
        if (solid && window.__strapHexOwner !== 'c3d') sessionStorage.setItem('mpbStrapHex', solid);
      } catch (e) {}
    }

    document.addEventListener("click", (e) => {
      if (e.target.closest && e.target.closest(".mpb-swatch")) setTimeout(paintStrapBackground, 30);
    });

    /* Strap colour chosen inside the modal drives the collar and leash too. */
    document.addEventListener("cikb:strapcolor", (e) => {
      const label = e.detail && e.detail.label;
      if (label && window.MPB && typeof window.MPB.setColor === "function") {
        window.MPB.setColor(label);
      }
      setTimeout(paintStrapBackground, 30);
    });

    /* Insert a symbol where the caret is, not at the end. */
    function insertAtCaret(ch) {
      dropAdopted();
      const value = String(input.value || "");
      const chars = Array.from(value);
      if (chars.length >= maxChars) return;
      const start = input.selectionStart == null ? value.length : input.selectionStart;
      const end   = input.selectionEnd == null ? start : input.selectionEnd;
      input.value = value.slice(0, start) + ch + value.slice(end);
      const caret = start + ch.length;
      try { input.setSelectionRange(caret, caret); } catch (e) {}
      input.focus();
      render();
    }

    SYMBOLS.forEach((sym) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "cni__symbol";
      b.innerHTML = `<span class="cni__symbol-ch">${sym.ch}</span>${sym.label}`;
      b.addEventListener("click", () => insertAtCaret(sym.ch));
      symbolsEl.appendChild(b);
    });

    const clearBtn = mount.querySelector("[data-cni-clear]");
    if (clearBtn) {
      clearBtn.addEventListener("click", () => {
        dropAdopted();
        input.value = "";
        mixedSeq.length = 0;      // next Mixed pick starts a fresh scatter
        render();
        input.focus();
      });
    }

    /* Restore whatever the customer had before the block was swapped out. */
    const prev = STORE.get(bid);
    if (prev) {
      if (typeof prev.name === "string") input.value = prev.name;
      if (Number.isInteger(prev.idx)) activeIdx = prev.idx;
      if (Array.isArray(prev.mixed)) { mixedSeq.length = 0; prev.mixed.forEach((x) => mixedSeq.push(x)); }
      /* Re-adopt the sets we staged before the swap, so they are replaced rather
       * than orphaned in CIKB alongside the new one. */
      if (prev.bundleId) bundleId = prev.bundleId;
      if (prev.adoptedId) adoptedId = prev.adoptedId;
      /* Add to cart CONSUMES the set, so the saved id can point at a bundle that no
       * longer exists. Left in place it makes stage() stand down for ever (it returns
       * early whenever adoptedId is truthy) and the next add is a bare collar. */
      if (adoptedId && window.CIKB && window.CIKB.getStagedBundles && !adoptedBundle()) {
        adoptedId = null;
      }
      /* Mixed is its own flag, not an index into the colour list, so restoring
       * activeIdx alone silently dropped it back to the first colour while the
       * staged set stayed mixed. */
      mixedMode = !!prev.mixedMode;
      const chips = colorsEl.querySelectorAll(".cni__chip");
      chips.forEach((c) => c.classList.remove("is-active"));
      /* Custom outranks both: its per-charm colours override the single-colour choice,
       * so restoring activeIdx over the top of it is what silently dropped a custom
       * strap back to a solid chip -- the same trap Mixed was pulled out of above. */
      if (adoptedBundle()) customBtn.classList.add("is-active");
      else if (mixedMode) mixedBtn.classList.add("is-active");
      else if (chips[activeIdx]) chips[activeIdx].classList.add("is-active");
    }
    setColorName(adoptedBundle() ? (root.dataset.customLabel || "Custom")
               : mixedMode ? "Mixed" : ((colors[activeIdx] || {}).title || ""));

    /* A real keystroke abandons the Custom set -- the customer is typing a new name and
     * the modal's per-charm colours no longer describe it. A SYNTHETIC input is the
     * re-stage signal used by Add to cart (sp:staged:consumed, below) and by the
     * free-leash offer; dropping the set on those is what turned a custom strap into a
     * single solid colour the moment the cart came back. dispatchEvent always reports
     * isTrusted false, so the two are told apart without a private flag. */
    input.addEventListener("input", (e) => {
      if (!e || e.isTrusted !== false) dropAdopted();
      render();
    });

    /* Add to cart CONSUMES the staged bundle -- the items have moved to the cart, so
     * CIKB pops them. The page was then left showing a name and a colour that nothing
     * was staged against: the render lost its charms, and adding again put a bare
     * collar in the cart. Re-stage what the builder still knows, so what is on screen
     * stays true and a second add is the same set. */
    if (!window.__cniRestageAfterAdd) {
      window.__cniRestageAfterAdd = 1;
      document.addEventListener("sp:staged:consumed", () => {
        setTimeout(() => {
          document.querySelectorAll(".cikb[data-bid]").forEach((r) => {
            const f = r.querySelector(".cni__input");
            if (f) f.dispatchEvent(new Event("input", { bubbles: true }));
          });
        }, 400);
      });
    }

    render();
    paintStrapBackground();
    setTimeout(paintStrapBackground, 400);
  }

  const boot = () => document.querySelectorAll('.cikb[data-bid]').forEach(init);
  /* …and watch for that teardown, so it is rebuilt the moment it is wiped. */
  /* A variant change REPLACES this block, flag and all. Watching the block itself
   * is useless once it has been swapped out -- the observer goes with it and the
   * replacement is never wired up, which is why the colour chips and symbol
   * buttons disappeared the first time a colour was picked. Watch a container
   * that outlives it instead, and re-run boot against whatever is there now. */
  let reTimer = 0;
  const watchRoots = () => {
    const host = document.querySelector('main-product-default') ||
                 document.querySelector('.product__details') || document.body;
    if (host.__cniWatched) return;
    host.__cniWatched = true;
    new MutationObserver(() => {
      clearTimeout(reTimer);
      reTimer = setTimeout(boot, 80);
    }).observe(host, { childList: true, subtree: true });
  };
  ["DOMContentLoaded", "shopify:section:load", "shopify:section:select"].forEach((e) =>
    document.addEventListener(e, boot)
  );
  if (document.readyState !== "loading") { boot(); watchRoots(); }
  document.addEventListener("DOMContentLoaded", watchRoots);
})();
